"use client";

import { getImageProps, getVideoUrl } from "@utilities/payload";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useMove } from "react-aria";
import type { Media } from "@mw/shared/payload-types";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { ImageSizes } from "@ui/lib/image";
import { BLOSSOM_NO_DRAG } from "@ui/components/layout/BlossomCarousel";

export interface BeforeAfterDemoProps {
  beforeImage?: Media | string | null;
  afterImage?: Media | string | null;
  beforeVideo?: Media | string | null | undefined;
  afterVideo?: Media | string | null | undefined;
  beforeLabel?: string | null;
  afterLabel?: string | null;
}

interface LabelProps {
  children: React.ReactNode;
  position: "left" | "right";
}

function Label({ children, position }: LabelProps) {
  return (
    <div
      className={clsx(
        "tw-absolute tw-top-4 tw-text-white tw-font-medium tw-type-xs tw-z-20 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-black/20 tw-backdrop-blur",
        position === "left" ? "tw-left-4" : "tw-right-4",
      )}
    >
      {children}
    </div>
  );
}

function getClipStyle(percentage: number, isDragging: boolean): React.CSSProperties {
  return {
    clipPath: `inset(0 ${100 - percentage * 100}% 0 0)`,
    transition: isDragging ? "none" : "clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  };
}

interface BackgroundVideoProps {
  src: string;
  ref?: React.Ref<HTMLVideoElement>;
  autoPlay?: boolean;
  style?: React.CSSProperties;
}

function BackgroundVideo({ src, ref, autoPlay, style }: BackgroundVideoProps) {
  return (
    <video
      ref={ref}
      src={src}
      autoPlay={autoPlay}
      loop
      muted
      playsInline
      preload='auto'
      className={clsx("tw-object-cover tw-absolute tw-inset-0 tw-w-full tw-h-full", style && "tw-overflow-hidden")}
      style={style}
    />
  );
}

interface SliderTrackProps {
  position: "top" | "bottom";
}

function SliderTrack({ position }: SliderTrackProps) {
  const isBottom = position === "bottom";

  return (
    <div
      className={clsx(
        "tw-absolute tw-left-1/2 tw-w-px tw-h-1/2 tw-transform -tw-translate-x-1/2 tw-transition-colors",
        isBottom
          ? "tw-bottom-0 tw-bg-gradient-to-b tw-from-white/60 tw-to-transparent group-focus-visible:tw-from-blue-500"
          : "tw-top-0 tw-bg-white/60 group-focus-visible:tw-bg-blue-500",
      )}
    />
  );
}

interface SliderProps {
  sliderValue: number;
  setSliderValue: React.Dispatch<React.SetStateAction<number>>;
  ariaLabel: string;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
}

function Slider({ sliderValue, setSliderValue, ariaLabel, isDragging, setIsDragging }: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const trackWidthRef = useRef<number>(0);

  const clamp = (value: number) => Math.max(0, Math.min(100, value));

  // Cache the track width on mount and when element resizes
  useEffect(() => {
    if (!trackRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        trackWidthRef.current = entry.contentRect.width;
      }
    });

    resizeObserver.observe(trackRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { moveProps } = useMove({
    onMoveStart: () => {
      setIsDragging(true);
    },
    onMoveEnd: () => {
      setIsDragging(false);
      // Clamp the value when drag ends
      setSliderValue(value => clamp(value));
    },
    onMove: e => {
      if (trackWidthRef.current === 0) return;
      if (e.pointerType === "keyboard") {
        // Keyboard events return -1/+1 for left/right, so we multiply by 5 to get a more reasonable value
        setSliderValue(value => clamp(value + e.deltaX * 5));
        return;
      }
      const deltaPercent = (e.deltaX / trackWidthRef.current) * 100;
      // Allow unclamped value during drag
      setSliderValue(value => value + deltaPercent);
    },
  });

  // Click to seek
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    // Only handle clicks directly on the track, not on the slider handle
    if (e.target !== trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    setSliderValue(clamp(percentage));
  };

  // Clamp value for rendering
  const clampedValue = clamp(sliderValue);

  return (
    // Click to seek is a mouse-only enhancement. Keyboard users interact with the fully accessible slider handle below.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div ref={trackRef} className='tw-absolute tw-inset-0 tw-z-10' onClick={handleTrackClick}>
      {/* Slider Control - Interactive Element */}
      <div
        ref={dividerRef}
        {...moveProps}
        {...{ [BLOSSOM_NO_DRAG]: "" }}
        className='tw-group tw-absolute tw-inset-y-0 tw-w-4 tw-cursor-col-resize focus-visible:tw-outline-none'
        style={{
          left: `${clampedValue}%`,
          transform: "translateX(-50%)",
          touchAction: "none",
          transition: isDragging ? "none" : "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        role='slider'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clampedValue)}
        aria-label={ariaLabel}
        tabIndex={0}
      >
        <SliderTrack position='top' />

        {/* Slider Handle */}
        <div
          className={clsx(
            "tw-absolute tw-top-1/2 tw-left-1/2 tw-w-6 tw-h-12 tw-backdrop-blur tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-select-none tw-transition-colors tw-z-10",
            "tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2",
            "group-focus-visible:tw-outline group-focus-visible:tw-outline-2 group-focus-visible:tw-outline-blue-500 group-focus-visible:tw-outline-offset-2",
            isDragging ? "tw-bg-white/60" : "tw-bg-white/40 group-hover:tw-bg-white/60",
          )}
        >
          <svg viewBox='0 0 20 20' fill='none' className='tw-relative tw-z-10 tw-size-5' aria-hidden='true'>
            <path
              d='M7.50002 13.4022L4.16669 10.0516L7.50002 6.70104M12.5 13.4022L15.8334 10.0516L12.5 6.70104'
              stroke='white'
              strokeWidth='1.66667'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <output className='tw-sr-only'>{Math.round(clampedValue)}%</output>
        </div>

        <SliderTrack position='bottom' />
      </div>
    </div>
  );
}

export function BeforeAfterDemo({
  beforeImage,
  afterImage,
  beforeVideo,
  afterVideo,
  beforeLabel,
  afterLabel,
}: BeforeAfterDemoProps) {
  const t = useTranslations("ClientKeys.BeforeAfterDemo");
  const [sliderValue, setSliderValue] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  // Video refs for synchronization
  const beforeVideoRef = useRef<HTMLVideoElement>(null);
  const afterVideoRef = useRef<HTMLVideoElement>(null);
  const videosReadyRef = useRef({ before: false, after: false });
  const videosStartedRef = useRef(false);

  // Clamp for rendering
  const clampedValue = Math.max(0, Math.min(100, sliderValue));
  const percentage = clampedValue / 100;

  const isVideoMode = beforeVideo && afterVideo;
  const isMixedMode = beforeImage && afterVideo && !beforeVideo && !afterImage;
  const beforeImageProps = !isVideoMode ? getImageProps(beforeImage, { placeholder: true, fill: true }) : null;
  const afterImageProps =
    !isVideoMode && !isMixedMode ? getImageProps(afterImage, { placeholder: true, fill: true }) : null;
  const beforeVideoUrl = isVideoMode ? getVideoUrl(beforeVideo) : null;
  const afterVideoUrl = isVideoMode ? getVideoUrl(afterVideo) : null;
  const mixedAfterVideoUrl = isMixedMode
    ? typeof afterVideo === "string"
      ? afterVideo
      : getVideoUrl(afterVideo)
    : null;

  // Synchronize video playback
  useEffect(() => {
    if (!isVideoMode) return;

    const beforeVideo = beforeVideoRef.current;
    const afterVideo = afterVideoRef.current;

    if (!beforeVideo || !afterVideo) return;

    // Reset ready state when videos change
    videosReadyRef.current = { before: false, after: false };
    videosStartedRef.current = false;

    const startBothVideos = () => {
      if (videosStartedRef.current) return; // Prevent multiple starts

      if (videosReadyRef.current.before && videosReadyRef.current.after) {
        // Start both videos at the same time
        videosStartedRef.current = true;
        beforeVideo.currentTime = 0;
        afterVideo.currentTime = 0;
        void beforeVideo.play();
        void afterVideo.play();
      }
    };

    const handleBeforeReady = () => {
      videosReadyRef.current.before = true;
      startBothVideos();
    };

    const handleAfterReady = () => {
      videosReadyRef.current.after = true;
      startBothVideos();
    };

    beforeVideo.addEventListener("canplay", handleBeforeReady);
    afterVideo.addEventListener("canplay", handleAfterReady);

    // Check if videos are already ready (e.g., cached)
    // readyState >= 3 means HAVE_FUTURE_DATA or higher
    if (beforeVideo.readyState >= 3) {
      handleBeforeReady();
    }
    if (afterVideo.readyState >= 3) {
      handleAfterReady();
    }

    return () => {
      beforeVideo.removeEventListener("canplay", handleBeforeReady);
      afterVideo.removeEventListener("canplay", handleAfterReady);
    };
  }, [isVideoMode, beforeVideoUrl, afterVideoUrl]);

  if (isVideoMode) {
    if (!beforeVideoUrl || !afterVideoUrl) {
      return null;
    }
  } else if (isMixedMode) {
    if (!beforeImageProps || !mixedAfterVideoUrl) {
      return null;
    }
  } else {
    if (!beforeImageProps || !afterImageProps) {
      return null;
    }
  }

  return (
    <div className='tw-absolute tw-inset-0 tw-overflow-hidden tw-z-0'>
      {isMixedMode ? (
        <>
          <BackgroundVideo ref={afterVideoRef} src={mixedAfterVideoUrl!} autoPlay />
          {beforeImageProps && (
            <Image
              {...beforeImageProps}
              className='tw-object-cover tw-absolute tw-inset-0 tw-overflow-hidden'
              sizes={ImageSizes.FULL_HALF_ON_LG}
              style={getClipStyle(percentage, isDragging)}
            />
          )}
        </>
      ) : isVideoMode ? (
        <>
          <BackgroundVideo ref={afterVideoRef} src={afterVideoUrl!} />
          <BackgroundVideo ref={beforeVideoRef} src={beforeVideoUrl!} style={getClipStyle(percentage, isDragging)} />
        </>
      ) : (
        <>
          {afterImageProps && (
            <Image
              {...afterImageProps}
              className='tw-object-cover tw-absolute tw-inset-0'
              sizes={ImageSizes.FULL_HALF_ON_LG}
            />
          )}
          {beforeImageProps && (
            <Image
              {...beforeImageProps}
              className='tw-object-cover tw-absolute tw-inset-0 tw-overflow-hidden'
              sizes={ImageSizes.FULL_HALF_ON_LG}
              style={getClipStyle(percentage, isDragging)}
            />
          )}
        </>
      )}

      {/* Labels */}
      <Label position='left'>{beforeLabel || t("input")}</Label>
      <Label position='right'>{afterLabel || t("output")}</Label>

      {/* Slider */}
      <Slider
        sliderValue={sliderValue}
        setSliderValue={setSliderValue}
        ariaLabel={t("slider_aria_label")}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
      />
    </div>
  );
}
